import {
  ComponentsOverrides,
  ComponentsProps,
  ComponentsVariants,
  Theme,
} from '@mui/material';
import {
  tannerBlack,
  tannerBlue,
  tannerGray,
  tannerPurple,
  tannerSemantic,
  tannerWhite,
} from '../colors';

type MuiButtonBase = {
  styleOverrides?: ComponentsOverrides<Theme>['MuiButtonBase'];
  defaultProps?: ComponentsProps['MuiButtonBase'];
  variants?: ComponentsVariants['MuiButtonBase'];
};
type MuiButton = {
  styleOverrides?: ComponentsOverrides<Theme>['MuiButton'];
  defaultProps?: ComponentsProps['MuiButton'];
  variants?: ComponentsVariants['MuiButton'];
};
type MuiButtonGroup = {
  styleOverrides?: ComponentsOverrides<Theme>['MuiButtonGroup'];
  defaultProps?: ComponentsProps['MuiButtonGroup'];
  variants?: ComponentsVariants['MuiButtonGroup'];
};

export const MuiButtonBase: MuiButtonBase = {
  styleOverrides: {
    root: {
      '&.MuiPickersDay-root.Mui-selected': {
        // Selected state styles
        '&:focus': {
          ':focus-visible': {
            backgroundColor: tannerPurple['600'],
            color: tannerWhite,
          },
        },
        '&:hover': {
          backgroundColor: tannerPurple['600'],
        },
      },
      '&.MuiPickersDay-dayWithMargin': {
        // Day with margin styles
        '&:focus': {
          ':focus-visible': {
            outline: `3px solid ${tannerSemantic.info.color}`,
            outlineOffset: 3,
            transition: 'outline-offset 150ms',
            backgroundColor: tannerGray['100'],
            zIndex: 1,
          },
        },
        '&.MuiPickersDay-today': {
          // Today's date styles
          border: '2px solid' + tannerWhite,
          outline: '1px solid' + tannerGray['300'],
          backgroundColor: tannerWhite,
          color: tannerBlack,
          '&:hover': {
            backgroundColor: tannerGray['100'],
          },
          '&:focus': {
            ':focus-visible': {
              outline: '2px solid' + tannerWhite,
              border: '1px solid' + tannerGray['300'],
              boxShadow: '0 0 0 4px ' + tannerSemantic.info.color,
              backgroundColor: tannerWhite,
              color: tannerBlack,
            },
            ':not(:focus-visible)': {
              backgroundColor: 'inherit',
              color: 'inherit',
            },
          },
        },
      },
    },
  },
};

export const MuiButton: MuiButton = {
  defaultProps: {
    variant: 'contained',
    color: 'primary',
    disableElevation: true,
    disableFocusRipple: true,
  },
  variants: [
    {
      props: { color: 'white' },
      style: {
        color: '#000',
        backgroundColor: tannerWhite,
        '&.Mui-disabled': {
          color: '#000',
          backgroundColor: tannerWhite,
        },
        '&:hover, &:focus-visible': {
          backgroundColor: tannerGray['200'],
        },
      },
    },
    {
      props: { color: 'black' },
      style: {
        color: tannerWhite,
        backgroundColor: '#000',
        '&.Mui-disabled': {
          color: tannerWhite,
          backgroundColor: '#000',
        },
        '&:hover, &:focus-visible': {
          backgroundColor: tannerGray['700'],
        },
      },
    },
    {
      props: { color: 'blue' },
      style: {
        color: tannerWhite,
        backgroundColor: tannerBlue['500'],
        '&.Mui-disabled': {
          color: '#D9E7FA',
          backgroundColor: '#668CEC',
        },
        ':disabled': {
          backgroundColor: '#BCD5F8',
          color: `${tannerSemantic.info.background}`,
        },
        '&:hover, &:focus-visible': {
          backgroundColor: '#0049AB',
        },
      },
    },
    {
      props: { color: 'error' },
      style: {
        backgroundColor: `${tannerSemantic.error.color}`,
        ':disabled': {
          backgroundColor: '#EC8D90',
          color: `${tannerSemantic.error.background}`,
        },
        '&:hover, &:focus-visible': {
          backgroundColor: '#B00007',
        },
      },
    },
  ],
  styleOverrides: {
    root: {
      fill: 'currentColor',
      borderRadius: 3,
      fontWeight: 800,
      fontSize: 14,
      padding: '7px 16px',
      textTransform: 'initial',
      minHeight: '40px',
      lineHeight: '140%',
      border: '2px solid transparent',
      '&.Mui-disabled': {
        opacity: 0.4,
      },
      '&:focus-visible': {
        outline: `3px solid ${tannerSemantic.info.color}`,
        outlineOffset: 3,
        transition: 'outline-offset 150ms',
      },
    },
    iconSizeMedium: {
      '& > *:first-of-type': {
        fontSize: 16,
      },
    },
    textSizeSmall: {
      minWidth: 0,
      padding: '0 8px',
      '&:hover:after': {
        backgroundColor: 'currentColor',
        content: '""',
        opacity: 0.2,
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        borderRadius: 3,
      },
    },
    text: {
      alignItems: 'center',
      borderRadius: 3,
      display: 'inline-flex',
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '140%',
      padding: '0 6px',
      '& .MuiButton-startIcon': {
        marginLeft: 0,
      },
      '& .MuiButton-endIcon': {
        marginRight: 0,
      },
    },
    containedPrimary: {
      '&.Mui-disabled': {
        backgroundColor: 'inherit',
      },
    },
    containedSecondary: {
      '&:hover, &:focus-visible': {
        backgroundColor: tannerGray['300'],
      },
    },
    textPrimary: {
      color: tannerSemantic.info.color,
      '&:hover, &:focus-visible': {
        backgroundColor: tannerSemantic.info.background,
      },
      '&.Mui-disabled': {
        color: tannerSemantic.info.color,
      },
    },
    textSecondary: {
      color: tannerGray['600'],
      '&:hover, &:focus-visible': {
        color: tannerGray['900'],
        backgroundColor: tannerGray['100'],
      },
      '&$disabled': {
        color: tannerGray['400'],
      },
    },
    colorSecondary: {
      color: tannerWhite,
    },
  },
};

export const MuiButtonGroup: MuiButtonGroup = {
  defaultProps: {
    variant: 'contained',
    color: 'primary',
    disableElevation: true,
    disableFocusRipple: true,
  },
  styleOverrides: {
    groupedContainedPrimary: {
      '&:last-child': {
        paddingLeft: 8,
        paddingRight: 8,
        minWidth: 34,
        margin: '2px 2px 2px 0',
      },
      '&:not(:last-child)': {
        borderColor: tannerWhite,
      },
    },
    groupedContainedSecondary: {
      '&:last-child': {
        paddingLeft: 8,
        paddingRight: 8,
        minWidth: 34,
        margin: '2px 2px 2px 0',
      },
      '&:not(:last-child)': {
        borderColor: tannerWhite,
      },
    },
  },
};
